import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import React from "react";
import { sizes, textStyle } from "./styles";

// The native counterpart types props from RN's <Text>, so callers may pass
// RN-only props (numberOfLines, pointerEvents, onPress). Those must not be
// forwarded to the DOM <span> — React errors on unknown attributes — so we
// translate them to their web equivalents here.
type ITextProps = React.ComponentProps<"span"> &
  VariantProps<typeof textStyle> & {
    numberOfLines?: number;
    pointerEvents?: React.CSSProperties["pointerEvents"];
    onPress?: React.MouseEventHandler<HTMLSpanElement>;
  };

const Text = React.forwardRef<React.ComponentRef<"span">, ITextProps>(
  function Text(
    {
      className,
      isTruncated,
      bold,
      underline,
      strikeThrough,
      size = "md",
      sub,
      italic,
      highlight,
      numberOfLines,
      pointerEvents,
      onPress,
      style,
      ...props
    }: { className?: string } & ITextProps,
    ref
  ) {
    const clampStyle: React.CSSProperties | undefined =
      numberOfLines && numberOfLines > 0
        ? {
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: numberOfLines,
            overflow: "hidden",
          }
        : undefined;

    return (
      <span
        className={textStyle({
          isTruncated: isTruncated as boolean,
          bold: bold as boolean,
          underline: underline as boolean,
          strikeThrough: strikeThrough as boolean,
          size: size as keyof typeof sizes,
          sub: sub as boolean,
          italic: italic as boolean,
          highlight: highlight as boolean,
          class: className,
        })}
        onClick={onPress}
        style={{
          ...style,
          ...(pointerEvents ? { pointerEvents } : null),
          ...clampStyle,
        }}
        {...props}
        ref={ref}
      />
    );
  }
);

Text.displayName = "Text";

export { Text };
